import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  COMPETITION_STORAGE_KEY,
  DEFAULT_COMPETITION_SLUG
} from '../constants/competition';
import { AuthLayout } from '../components/layout/AuthLayout';
import { FormInput } from '../components/ui/FormInput';
import { Button } from '../components/ui/Button';
import toast from 'react-hot-toast';
import { Mail, Lock, User, UserPlus } from 'lucide-react';

const Register = () => {
  const { register } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error(language === 'en' ? 'Passwords do not match' : 'הסיסמאות אינן תואמות');
      return;
    }

    setLoading(true);

    try {
      const success = await register(formData.fullName, formData.email, formData.password);
      if (success) {
        toast.success(language === 'en' ? 'Registration successful!' : 'הרשמה הצליחה!');
        try {
          const s = localStorage.getItem(COMPETITION_STORAGE_KEY) || DEFAULT_COMPETITION_SLUG;
          navigate(`/c/${s}/dashboard`);
        } catch {
          navigate(`/c/${DEFAULT_COMPETITION_SLUG}/dashboard`);
        }
      }
    } catch (error) {
      console.error('Unexpected registration error:', error);
      toast.error(language === 'en' ? 'An unexpected error occurred' : 'אירעה שגיאה בלתי צפויה');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <AuthLayout
      title={language === 'en' ? 'Join Football Bets' : 'הצטרף ל-Football Bets'}
      subtitle={
        language === 'en' ? 'Create your account to start betting' : 'צור חשבון כדי להתחיל להמר'
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <FormInput
          id="fullName"
          name="fullName"
          type="text"
          label={t('fullName')}
          value={formData.fullName}
          onChange={handleChange}
          required
          autoComplete="name"
          placeholder={language === 'en' ? 'Enter your full name' : 'הכנס את השם המלא שלך'}
          icon={<User className="h-5 w-5" />}
          isRTL={isRTL}
        />

        <FormInput
          id="email"
          name="email"
          type="email"
          label={t('email')}
          value={formData.email}
          onChange={handleChange}
          required
          autoComplete="email"
          placeholder={language === 'en' ? 'Enter your email' : 'הכנס את האימייל שלך'}
          icon={<Mail className="h-5 w-5" />}
          isRTL={isRTL}
        />

        <FormInput
          id="password"
          name="password"
          type="password"
          label={t('password')}
          value={formData.password}
          onChange={handleChange}
          required
          autoComplete="new-password"
          placeholder={language === 'en' ? 'Enter your password' : 'הכנס את הסיסמה שלך'}
          icon={<Lock className="h-5 w-5" />}
          isRTL={isRTL}
          dir="ltr"
        />

        <FormInput
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          label={t('confirmPassword')}
          value={formData.confirmPassword}
          onChange={handleChange}
          required
          autoComplete="new-password"
          placeholder={language === 'en' ? 'Confirm your password' : 'אשר את הסיסמה שלך'}
          icon={<Lock className="h-5 w-5" />}
          isRTL={isRTL}
          dir="ltr"
        />

        <Button type="submit" size="lg" loading={loading} className="w-full">
          <UserPlus className="h-5 w-5" />
          {loading
            ? language === 'en'
              ? 'Creating account…'
              : 'יוצר חשבון…'
            : t('signUp')}
        </Button>
      </form>

      <p className="mt-6 border-t border-zinc-100 pt-6 text-center text-sm text-zinc-600">
        {t('alreadyHaveAccount')}{' '}
        <Link to="/login" className="font-semibold text-brand-600 hover:text-brand-700">
          {t('signIn')}
        </Link>
      </p>
    </AuthLayout>
  );
};

export default Register;
